import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '../auth';

// Cache global para páginas pré-carregadas
const preloadCache = new Map<string, {
  data: string[];
  timestamp: number;
}>();

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos

/**
 * Hook para pré-carregar páginas da organização em background
 * Melhora a performance do sidebar evitando delay no carregamento
 */
export function usePreloadPages() {
  const orgId = useOrgId();

  useEffect(() => {
    if (!orgId) return;

    // Verificar se já está no cache
    const cached = preloadCache.get(orgId);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return;
    }

    // Pré-carregar em background
    const preloadPages = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_organization_pages' as any, { org_id: orgId });

        if (!error && data && Array.isArray(data)) {
          const routes = data
            .filter((page: any) => page.is_enabled)
            .map((page: any) => page.route);
          
          // Salvar no cache
          preloadCache.set(orgId, {
            data: routes,
            timestamp: now
          });
        }
      } catch (error) {
        // Ignora erros de preload (não crítico)
      }
    };

    // Executar em background com delay pequeno para não bloquear UI
    const timeoutId = setTimeout(preloadPages, 100);
    
    return () => clearTimeout(timeoutId);
  }, [orgId]);

  // Função para limpar cache (útil para logout)
  const clearCache = () => {
    preloadCache.clear();
  };

  return { clearCache };
}
